/* eslint-disable @typescript-eslint/no-explicit-any */
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { useStore } from '~/app/store'
import { UserEdit } from '~/entities/users/user-edit'
import { usePageTitle, useToast } from '~/shared/lib'
import { Button, IconSave, MaxWidthContainer, PageTitle } from '~/shared/ui'
import Spinner from '~/shared/ui/spinner'
import { PrintersType } from '~/shared/api/services/users/types'
import { UsersService } from '~/shared/api/services/users'
import { useErrorHandler } from '~/shared/lib/hooks/use-error-handler'

export const UserEditFeature = observer(() => {
  const { t } = useTranslation(['common', 'profile'])
  const { usersStore, printerStore } = useStore()
  const { loaders, setRoles, editedUser, selectedRoles } = usersStore
  const { printersUser, modifiedPrinterFields, clearModifiedPrinterFields } =
    printerStore
  const toast = useToast()
  const { handleErrorWithToast } = useErrorHandler()
  const { id } = useParams()

  const [isSubmitting, setIsSubmitting] = useState(false)

  usePageTitle({
    title: t('profile:editProfile'),
  })

  const rolesChanged = useMemo(() => {
    const currentRoleNames = editedUser?.roles?.map((role) => role.id) || []
    const selectedRoleNames = selectedRoles.map((role) => role.value)

    return (
      currentRoleNames.length !== selectedRoleNames.length ||
      !currentRoleNames.every((roleName) =>
        selectedRoleNames.includes(roleName),
      ) ||
      !selectedRoleNames.every((roleName) =>
        currentRoleNames.includes(roleName),
      )
    )
  }, [editedUser?.roles, selectedRoles])

  const handleSave = async (e: any) => {
    try {
      setIsSubmitting(true)
      usersStore.tempHandlers.forEach((handler) => handler(e))

      if (printersUser && editedUser?.id) {
        try {
          const printersArray = Object.entries(printersUser).map(
            ([key, valueObj]) => ({
              [key]: valueObj.value,
            }),
          ) as PrintersType

          if (modifiedPrinterFields.length > 0) {
            await UsersService.editPrintersUser(editedUser.id, printersArray)

            toast.open(t('profile:printerSettingsSaved'), 'success')
            clearModifiedPrinterFields()
          }
        } catch (error) {
          toast.open(t('profile:printerSettingsEditError'), 'danger')
        }
      }

      if (rolesChanged && editedUser && editedUser.roles && id) {
        await setRoles(
          id,
          editedUser?.roles.map((el) => el.name),
        )

        toast.open(t('profile:rolesUpdatedSuccess'), 'success')
      }
    } catch (error) {
      handleErrorWithToast(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <MaxWidthContainer>
      <div className="flex justify-between items-center pb-6">
        <PageTitle
          withBackButton
          title={t('profile:editProfile')}
          size="text-[24px]"
          color="text-[#242424]"
        />

        <Button intent="success" onClick={handleSave}>
          {loaders.setRoles || isSubmitting ? <Spinner /> : <IconSave />}
          {t('save')}
        </Button>
      </div>
      <UserEdit />
    </MaxWidthContainer>
  )
})
